import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PROGRAM_LABELS } from "@/lib/calendar";

export const SUBJECTS = [["none", "No subject"], ["maths", "Maths"], ["english", "English"], ["science", "Science"], ["writing", "Writing"]];

export function ProgramPicker({ value, onChange, prefix }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((p) => p !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-3">
      {Object.entries(PROGRAM_LABELS).map(([k, l]) => (
        <label key={k} className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A1530]">
          <Checkbox checked={value.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`${prefix}-program-${k}`} /> {l}
        </label>
      ))}
    </div>
  );
}

const blank = (date) => ({ title: "", type: "mock", date, start: "", end: "", programs: [], subject: "none", notes: "" });

export function EventForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(blank(""));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initial) setF({ ...blank(initial.date), ...initial, subject: initial.subject || "none", start: initial.start || "", end: initial.end || "" }); }, [initial]);
  if (!initial) return null;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    if (!f.title.trim() || !f.date || !f.programs.length) return toast.error("Add a title, date and at least one program.");
    if ((f.start && !f.end) || (!f.start && f.end)) return toast.error("Add both a start and end time, or leave both empty for all day.");
    setBusy(true);
    const body = { title: f.title.trim(), type: f.type, date: f.date, start: f.start || null, end: f.end || null, programs: f.programs, subject: f.subject === "none" ? null : f.subject, notes: f.notes };
    try {
      if (initial.id) await api.put(`/calendar/events/${initial.id}`, body);
      else await api.post("/calendar/events", body);
      toast.success("Event saved."); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save event."); }
    finally { setBusy(false); }
  };
  const remove = async () => { await api.delete(`/calendar/events/${initial.id}`); toast.success("Event deleted."); onSaved(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="mn max-h-[90vh] max-w-lg overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{initial.id ? "Edit event" : "Add event"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="event-title-input" value={f.title} onChange={set("title")} placeholder="e.g. Full JMSS mock exam" className="mt-1.5 h-11 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={set("type")}>
                <SelectTrigger data-testid="event-type-select" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mock">Mock exam</SelectItem><SelectItem value="key_date">Key exam date</SelectItem><SelectItem value="class">Extra class</SelectItem><SelectItem value="other">Other event</SelectItem></SelectContent>
              </Select></div>
            <div><Label>Subject</Label>
              <Select value={f.subject} onValueChange={set("subject")}>
                <SelectTrigger data-testid="event-subject-select" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{SUBJECTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date</Label><Input type="date" data-testid="event-date-input" value={f.date} onChange={set("date")} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label>Start</Label><Input type="time" data-testid="event-start-input" value={f.start} onChange={set("start")} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label>End</Label><Input type="time" data-testid="event-end-input" value={f.end} onChange={set("end")} className="mt-1.5 h-11 rounded-xl" /></div>
          </div>
          <p className="text-xs text-[#948EAB]">Leave times empty for an all-day event. Melbourne time.</p>
          <div><Label>Show to</Label><div className="mt-2"><ProgramPicker value={f.programs} onChange={set("programs")} prefix="event" /></div></div>
          <div><Label>Notes</Label><Textarea data-testid="event-notes-input" value={f.notes} onChange={set("notes")} className="mt-1.5 rounded-xl" rows={3} /></div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {initial.id ? <Button variant="outline" onClick={remove} data-testid="event-delete-btn" className="rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50">Delete</Button> : <span />}
          <Button onClick={save} disabled={busy} data-testid="event-save-btn" className="rounded-xl ember-gradient font-bold">{busy ? "Saving…" : "Save event"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
